import React from 'react';
import TableActions, { TableActionConfig } from './TableActions';

/**
 * Props pour le composant TableSection
 */
interface TableSectionProps {
  /** Configuration des actions de table */
  actionsConfig: TableActionConfig;
  /** Filtres de la table */
  filters?: React.ReactNode;
  /** Contenu de la table */
  children: React.ReactNode;
  /** Classe CSS supplémentaire */
  className?: string;
  /** Barre de filtres sticky au scroll (évite le retour en haut quand on tape dans la recherche) */
  stickyFilters?: boolean;
}

/**
 * Composant pour organiser une section de table avec filtres et actions
 * 
 * @example
 * ```tsx
 * <TableSection
 *   actionsConfig={{
 *     newButtonKey: 'leads.createLead',
 *     onCreateNew: handleCreateLead,
 *     onExport: handleExport,
 *     onImport: handleImport
 *   }}
 *   filters={<LeadFilters />}
 * >
 *   <LeadTable />
 * </TableSection>
 * ```
 */
const TableSection: React.FC<TableSectionProps> = ({
  actionsConfig,
  filters,
  children,
  className = '',
  stickyFilters = false,
}) => {
  const hasActions = Boolean(
    actionsConfig.newButtonKey ||
    actionsConfig.onExport ||
    actionsConfig.onImport ||
    actionsConfig.customActions
  );

  return (
    <div className={`flex flex-col min-h-0 ${className}`}>
      {/* Actions et filtres */}
      <div className={hasActions && filters ? "space-y-2 sm:space-y-3 flex-shrink-0" : "flex-shrink-0"}>
        {/* Actions (New, Export, Import) */}
        {hasActions && <TableActions config={actionsConfig} />}
        
        {/* Filtres — sticky évite le scroll en haut quand on tape */}
        {filters && (
          <div
            className={
              stickyFilters
                ? "sticky top-0 z-10 rounded-xl bg-[var(--color-bg-primary)] dark:bg-gray-800 px-2 pb-2 sm:px-3 sm:pb-3"
                : "bg-gray-50 dark:bg-gray-800 rounded-xl px-2 pb-2 sm:px-3 sm:pb-3"
            }
          >
            {filters}
          </div>
        )}
      </div>

      {/* Contenu de la table */}
      <div className="flex-1 min-h-0">
        {children}
      </div>
    </div>
  );
};

export default TableSection;
